import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import DateTimePicker from "../components/DateTimePicker";
import { Star, MapPin, Users, Settings, ArrowRight, Shield, BadgeCheck, Wrench, Headphones, RefreshCw, AlertTriangle, Sparkles, Truck, Lock } from "lucide-react";
import { toast } from "sonner";
import PriceInsight from "../components/PriceInsight";
import SmartReschedule from "../components/SmartReschedule";
import LateReturnWarning, { isAfterHoursSlot, AFTER_HOURS_FEE } from "../components/LateReturnWarning";
import { parseDateTimeLocal, formatYMD, rangeOverlapsRanges, rangeHitsHostCalendar, billableDays, bookingWindowError, nextTimeSlotFromNow, alignReturnWithPickup, syncReturnAfterPickupChange, extractTime, defaultReturnForPickup, minPickupWithLeadDays, pickupLeadTimeError, formatReviewMonthYear, formatTripRange } from "../lib/dateLocal";
import { formatMoney, formatNumber, vehicleCurrency } from "../lib/currencyDisplay";
import { setReturnTo } from "../lib/returnTo";
import { useTripQuote } from "../lib/useTripQuote";
import { formatKmPolicyLine, hasKmPolicy } from "../lib/kmUnits";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import ListingImage from "../components/ListingImage";
import { getDayBreakdown, getListPrice, RATE_VARIANCE_HINT } from "../lib/tripPricingDisplay";
import TripChargesGroup from "../components/TripChargesGroup";
import { formatBookingError } from "../lib/bookingErrors";

const TENURE_PRESETS = [1, 3, 7, 14, 30];

// Normalize an incoming URL value: "YYYY-MM-DD" → "YYYY-MM-DDT10:00"; passes datetimes through unchanged.
function withDefaultTime(value, defaultTime) {
  if (!value) return "";
  return value.includes("T") ? value : `${value}T${defaultTime}`;
}

export default function VehicleDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [v, setV] = useState(null);
  const [start, setStart] = useState(() => {
    const raw = searchParams.get("start");
    if (!raw) return "";
    return withDefaultTime(raw, extractTime(raw, extractTime(nextTimeSlotFromNow())));
  });
  const [end, setEnd] = useState(() => {
    const rawStart = searchParams.get("start");
    const rawEnd = searchParams.get("end");
    if (!rawStart || !rawEnd) return "";
    const startVal = withDefaultTime(rawStart, extractTime(rawStart, extractTime(nextTimeSlotFromNow())));
    return alignReturnWithPickup(startVal, withDefaultTime(rawEnd, extractTime(startVal)));
  });
  const [tenure, setTenure] = useState(null);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [calendarMode, setCalendarMode] = useState("block_unavailable");
  const [calendarDays, setCalendarDays] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const minPickup = useMemo(
    () => minPickupWithLeadDays(v?.delivery_lead_days ?? 0),
    [v?.delivery_lead_days],
  );

  useEffect(() => {
    (async () => {
      try {
        const [vr, ar] = await Promise.all([
          axios.get(`${API}/vehicles/${id}`),
          axios.get(`${API}/vehicles/${id}/availability`).catch(() => ({ data: { booked_ranges: [] } })),
        ]);
        setV(vr.data);
        setBookedRanges(ar.data?.booked_ranges || []);
        setCalendarMode(ar.data?.calendar_mode || "block_unavailable");
        setCalendarDays(ar.data?.calendar_days || ar.data?.blocked_days || []);
      } catch (e) {
        toast.error("Vehicle not found");
        navigate("/browse");
      }
    })();
  }, [id, navigate]);

  // tenure preset → pick-up now, return +n×24h at the same clock time
  const applyTenure = (n) => {
    setTenure(n);
    const startVal = nextTimeSlotFromNow();
    setStart(startVal);
    setEnd(defaultReturnForPickup(startVal, n * 24));
  };

  const onStartChange = (newStart) => {
    const prev = start;
    setStart(newStart);
    if (!newStart) return;
    if (tenure) {
      setEnd(defaultReturnForPickup(newStart, tenure * 24));
      return;
    }
    setEnd(syncReturnAfterPickupChange(newStart, end, prev));
  };

  // A manual end-date change is a deliberate override → drop the preset.
  const onEndChange = (newEnd) => {
    setEnd(newEnd);
    setTenure(null);
  };

  const bookedConflict = useMemo(() => {
    if (!start || !end) return false;
    return rangeOverlapsRanges(start, end, bookedRanges);
  }, [start, end, bookedRanges]);

  const hostConflict = useMemo(() => {
    if (!start || !end) return false;
    return rangeHitsHostCalendar(start, end, calendarMode, calendarDays);
  }, [start, end, calendarMode, calendarDays]);

  const conflict = bookedConflict || hostConflict;

  const conflictMessage = hostConflict
    ? (calendarMode === "define_rentable"
      ? formatBookingError("dates_not_rentable")
      : formatBookingError("dates_blocked_by_host"))
    : "Not available on these dates. Greyed-out days are booked or blocked by the host.";

  const windowError = useMemo(
    () => bookingWindowError(start, end) || pickupLeadTimeError(start, v?.delivery_lead_days ?? 0),
    [start, end, v?.delivery_lead_days],
  );

  const { quote: tripQuote } = useTripQuote(id, start, end, {
    pickupLocation: v?.location_city,
    enabled: !!(id && start && end && !windowError && !conflict),
  });

  if (!v) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-[var(--zc-text-2)]" data-testid="vd-loading">Loading…</div>;

  const days = billableDays(start, end);
  const breakdown = tripQuote?.price_breakdown;
  const dayBreakdown = getDayBreakdown(tripQuote);
  const listPrice = getListPrice(v, tripQuote);
  const subtotal = tripQuote?.subtotal ?? days * v.daily_rate;
  const avgDaily = tripQuote?.avg_daily_rate;
  const dynamicRates = tripQuote && Math.abs(subtotal - days * v.daily_rate) > 0.01;
  const showListVsAvg = dayBreakdown.length > 0 && avgDaily != null
    && (listPrice.isPromo || Math.abs(listPrice.value - avgDaily) > 0.01);
  const promoOrig = listPrice.isPromo ? listPrice.value : null;
  const taxesAndFees = breakdown?.taxes_and_fees ?? 0;
  const taxAmount = breakdown?.tax_amount ?? 0;
  const otherFees = breakdown?.other_fees ?? breakdown?.platform_fee ?? 0;
  const quotedPickupAfterHours = breakdown?.after_hours_pickup_fee ?? 0;
  const quotedReturnAfterHours = breakdown?.after_hours_return_fee ?? 0;
  const opOpen = breakdown?.operating_open ?? v?.operating_open;
  const opClose = breakdown?.operating_close ?? v?.operating_close;
  const listingAhFee = breakdown?.listing_after_hours_fee ?? v?.after_hours_fee;
  const ahFeePerSlot = listingAhFee != null ? listingAhFee : AFTER_HOURS_FEE;
  const afterHoursPickupFee = quotedPickupAfterHours > 0
    ? quotedPickupAfterHours
    : (start && isAfterHoursSlot(start, opOpen, opClose) ? ahFeePerSlot : 0);
  const afterHoursReturnFee = quotedReturnAfterHours > 0
    ? quotedReturnAfterHours
    : (end && isAfterHoursSlot(end, opOpen, opClose) ? ahFeePerSlot : 0);
  const afterHoursFee = afterHoursPickupFee + afterHoursReturnFee;
  const afterHoursProvisional = (
    (afterHoursPickupFee > 0 && quotedPickupAfterHours === 0)
    || (afterHoursReturnFee > 0 && quotedReturnAfterHours === 0)
  );
  const provisionalAdd = (quotedPickupAfterHours === 0 ? afterHoursPickupFee : 0)
    + (quotedReturnAfterHours === 0 ? afterHoursReturnFee : 0);
  const estimatedTotal = days > 0
    ? (tripQuote?.estimated_total != null
      ? tripQuote.estimated_total + provisionalAdd
      : subtotal + taxesAndFees + afterHoursFee)
    : 0;
  const currency = vehicleCurrency(v);
  const money = (amount) => formatMoney(amount, currency);

  const proceed = () => {
    if (!start || !end) { toast.error("Select pick-up and return"); return; }
    if (windowError) { toast.error(windowError); return; }
    if (conflict) { toast.error(conflictMessage); return; }
    const target = `/book/${id}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
    if (!user) {
      setReturnTo(target);
      toast.message("Sign in to continue your booking");
      navigate("/login", { state: { authRequired: true } });
      return;
    }
    navigate(target);
  };

  const bookDisabled = conflict || !!windowError || days === 0;
  const bookLabel = windowError
    ? "Adjust trip dates"
    : conflict
      ? "Not available on these dates"
      : days === 0
        ? "Select dates to book"
        : "Book now";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 pb-24 lg:pb-12 min-w-0" data-testid="vehicle-details-page">
      {/* Breadcrumb */}
      <div className="text-xs text-[var(--zc-text-3)] mb-6 truncate">
        Home <span className="mx-1.5">›</span> Cars <span className="mx-1.5">›</span>
        <span className="text-[var(--zc-text)]">{v.year} {v.make} {v.model}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-10 min-w-0">
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="eyebrow mb-2">{v.vehicle_type}</div>
              <h1 className="zc-headline text-3xl sm:text-4xl md:text-5xl tracking-tight break-words" data-testid="vd-title">{v.year} {v.make} {v.model}</h1>
              <div className="flex flex-wrap items-center gap-5 text-sm text-[var(--zc-text-2)] mt-3">
                {(v.rating_count > 0) && (
                  <div className="flex items-center gap-1"><Star size={12} className="text-[var(--zc-accent)]" fill="currentColor" /><span className="text-[var(--zc-text)] font-semibold">{formatNumber(v.avg_rating, 1)}</span> <span className="text-[var(--zc-text-3)]">({v.rating_count})</span></div>
                )}
                <div className="flex items-center gap-1"><MapPin size={12} /> {v.location_city}</div>
                <div className="flex items-center gap-1"><Users size={12} /> {v.seats} seats</div>
                <div className="flex items-center gap-1"><Settings size={12} /> {v.transmission}</div>
                {v.powertrain && v.powertrain !== "gasoline" && (
                  <div className="flex items-center gap-1 capitalize">{v.powertrain === "phev" ? "Plug-in hybrid" : v.powertrain}</div>
                )}
                {v.delivery_lead_days > 0 && (
                  <div className="flex items-center gap-1 text-[var(--zc-text-2)]">
                    <Truck size={12} className="text-[var(--zc-accent)]" />
                    Ready in ~{v.delivery_lead_days} day{v.delivery_lead_days === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              {(hasKmPolicy(v) || v.allows_one_way) && (
                <div className="mt-3 text-xs text-[var(--zc-text-3)] space-y-1">
                  {hasKmPolicy(v) && (
                    <div>{formatKmPolicyLine(v)}</div>
                  )}
                  {v.allows_one_way && (
                    <div>One-way return available · {money(v.one_way_fee ?? 30)} fee</div>
                  )}
                </div>
              )}
            </div>
            <div className="w-full sm:w-auto sm:text-right shrink-0" data-testid="vd-price-header">
              {promoOrig != null && (
                <div className="text-sm text-[var(--zc-text-3)] line-through mb-0.5">
                  {money(promoOrig)}<span className="zc-unit text-xs"> /day</span>
                </div>
              )}
              <div className="zc-price text-2xl sm:text-3xl md:text-4xl">
                {days > 0 && avgDaily != null ? (
                  <>
                    {money(avgDaily)}
                    <span className="zc-unit text-sm sm:text-base normal-case tracking-normal"> /day avg</span>
                  </>
                ) : (
                  <>
                    from {money(v.daily_rate)}
                    <span className="zc-unit text-sm sm:text-base normal-case tracking-normal"> /day</span>
                  </>
                )}
              </div>
              <div className="zc-meta mt-1 max-w-full sm:max-w-[260px] sm:ml-auto break-words" title={RATE_VARIANCE_HINT}>
                {days > 0 && avgDaily != null && Math.abs(avgDaily - v.daily_rate) > 0.01 ? (
                  <>Host list price {money(v.daily_rate)}/day · {RATE_VARIANCE_HINT.toLowerCase()}</>
                ) : (
                  RATE_VARIANCE_HINT
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="aspect-[16/9] zc-card overflow-hidden" data-testid="vd-gallery">
              <ListingImage images={v?.images} alt="" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="mt-12">
            <div className="eyebrow mb-3">About this vehicle</div>
            <p className="text-[var(--zc-text)] leading-relaxed max-w-2xl">{v.description}</p>
          </div>

          {v.features?.length > 0 && (
            <div className="mt-10">
              <div className="eyebrow mb-3">Features</div>
              <div className="flex flex-wrap gap-2">
                {v.features.map((f, i) => (
                  <span key={i} className="px-3 py-1.5 text-xs zc-chip" data-testid={`vd-feature-${i}`}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* What's included */}
          <div className="mt-12">
            <div className="eyebrow mb-2">What's included</div>
            <h3 className="zc-headline text-2xl mb-5">Every trip, taken care of.</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { i: <Wrench size={16} />, t: "Servicing & maintenance" },
                { i: <Lock size={16} />, t: "Escrow-protected checkout" },
                { i: <Shield size={16} />, t: "Comprehensive insurance" },
                { i: <BadgeCheck size={16} />, t: "Verified host & vehicle" },
                { i: <Headphones size={16} />, t: "Dedicated support" },
                { i: <RefreshCw size={16} />, t: "Flexible cancellation" },
              ].map((x, i) => (
                <div key={i} className="zc-card p-4 flex items-center gap-3 border-2 border-[var(--zc-accent-soft)]">
                  <div className="w-9 h-9 grid place-items-center bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] rounded-lg">{x.i}</div>
                  <div className="text-sm">{x.t}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <div className="eyebrow mb-3">Reviews ({v.reviews?.length || 0})</div>
            {v.reviews?.length ? (
              <div className="space-y-4" data-testid="vd-reviews">
                {v.reviews.map(r => (
                  <div key={r.review_id} className="zc-card p-5 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{r.user_name}</div>
                        {r.created_at && (
                          <div className="text-xs text-[var(--zc-text-3)] mt-0.5">{formatReviewMonthYear(r.created_at)}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs shrink-0">
                        <Star size={12} className="text-[var(--zc-accent)]" fill="currentColor" />
                        <span>{r.rating}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[var(--zc-text-2)] mt-2 break-words">{r.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--zc-text-3)]">No reviews yet.</div>
            )}
          </div>
        </div>

        {/* Booking aside */}
        <aside className="lg:sticky lg:top-24 h-fit zc-card p-6 min-w-0">
          <div className="mt-1">
            <PriceInsight kind="vehicle" itemId={id} unit="/day" />
          </div>

          <div className="eyebrow mt-6 mb-3">Rental Model</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 sm:gap-2">
            {TENURE_PRESETS.map(n => (
              <button
                key={n}
                onClick={() => applyTenure(n)}
                type="button"
                className={"h-10 sm:h-11 text-xs sm:text-sm zc-tenure " + (tenure === n ? "zc-tenure-active" : "")}
                data-testid={`tenure-${n}`}
              >
                {n}d
              </button>
            ))}
          </div>
          <div className="zc-hint mt-2">Or pick custom dates below</div>

          <div className="grid grid-cols-1 gap-3 mt-4">
            <DateTimePicker
              label="Pick-up"
              value={start}
              onChange={onStartChange}
              minDate={minPickup}
              bookedRanges={bookedRanges}
              calendarMode={calendarMode}
              calendarDays={calendarDays}
              testid="vd-start-date"
              priceVehicleId={id}
              helperText="Tap the date to pick on the calendar. Greyed days are booked or blocked by the host. Coloured dots show your per-day rate (weekends & local demand)."
            />
            <DateTimePicker
              label="Return"
              value={end}
              onChange={onEndChange}
              minDate={start || undefined}
              defaultTime={start ? extractTime(start) : undefined}
              bookedRanges={bookedRanges}
              calendarMode={calendarMode}
              calendarDays={calendarDays}
              testid="vd-end-date"
              priceVehicleId={id}
              helperText={start ? "Return uses the same clock time as pick-up by default." : "Pick a return date & time after pick-up."}
            />
          </div>

          {windowError && (
            <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-error, #dc2626)" }} data-testid="vd-window-error">
              <AlertTriangle size={14} /> {windowError}
            </div>
          )}
          {conflict && (
            <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-error, #dc2626)" }} data-testid="vd-conflict-banner">
              <AlertTriangle size={14} /> {conflictMessage}
            </div>
          )}
          <LateReturnWarning pickupAt={start} returnAt={end} operatingOpen={opOpen} operatingClose={opClose} afterHoursFeeAmount={listingAhFee} />

          <SmartReschedule
            vehicleId={id}
            start={start}
            end={end}
            bookedRanges={bookedRanges}
            calendarMode={calendarMode}
            calendarDays={calendarDays}
            onApply={(s, e) => { setStart(s); setEnd(e); setTenure(null); }}
          />

          {days > 0 ? (
            <div className="mt-5 pt-5 border-t border-[var(--zc-border)] space-y-2 text-sm" data-testid="vd-summary">
              <TripChargesGroup
                money={money}
                days={days}
                subtotal={subtotal}
                avgDaily={avgDaily}
                dayBreakdown={dayBreakdown}
                dailyRate={v?.daily_rate}
                listPrice={listPrice}
                showListVsAvg={showListVsAvg}
                testid="vd-summary-charges"
              />
              {afterHoursPickupFee > 0 && (
                <div className="flex justify-between gap-3 text-[var(--zc-text-2)]" data-testid="vd-summary-after-hours-pickup">
                  <span className="min-w-0">
                    After-hours pick-up{opOpen && opClose ? ` (outside ${opOpen}–${opClose})` : " (10pm–6am)"}
                    {afterHoursProvisional && quotedPickupAfterHours === 0 && (
                      <span className="block zc-meta font-normal mt-0.5">Estimated until quote refreshes</span>
                    )}
                  </span>
                  <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(afterHoursPickupFee)}</span>
                </div>
              )}
              {afterHoursReturnFee > 0 && (
                <div className="flex justify-between gap-3 text-[var(--zc-text-2)]" data-testid="vd-summary-after-hours">
                  <span className="min-w-0">
                    After-hours return{opOpen && opClose ? ` (outside ${opOpen}–${opClose})` : " (10pm–6am)"}
                    {afterHoursProvisional && quotedReturnAfterHours === 0 && (
                      <span className="block zc-meta font-normal mt-0.5">Estimated until quote refreshes</span>
                    )}
                  </span>
                  <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(afterHoursReturnFee)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div className="flex justify-between gap-3 text-[var(--zc-text-2)]">
                  <span className="min-w-0">
                    Tax{breakdown?.tax_rate_percent != null ? ` (${breakdown.tax_rate_percent}%)` : ""}
                  </span>
                  <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(taxAmount)}</span>
                </div>
              )}
              {otherFees > 0 && (
                <div className="flex justify-between gap-3 text-[var(--zc-text-2)]">
                  <span className="min-w-0">
                    Service &amp; other fees
                    <span className="block zc-meta font-normal mt-0.5">Booking support &amp; platform services</span>
                  </span>
                  <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(otherFees)}</span>
                </div>
              )}
              {taxesAndFees > 0 && taxAmount === 0 && otherFees === 0 && (
                <div className="flex justify-between gap-3 text-[var(--zc-text-2)]">
                  <span className="min-w-0">Taxes &amp; fees</span>
                  <span className="text-[var(--zc-text)] font-semibold shrink-0">+{money(taxesAndFees)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline gap-3 pt-2 border-t border-[var(--zc-border)] mt-2">
                <span className="font-semibold shrink-0">Estimated total</span>
                <span className="zc-price text-xl sm:text-2xl shrink-0">{money(estimatedTotal)}</span>
              </div>
            </div>
          ) : (
            <div className="mt-5 pt-5 border-t border-[var(--zc-border)] text-sm text-[var(--zc-text-3)]" data-testid="vd-summary-empty">
              Select pick-up and return dates to see your trip total.
            </div>
          )}

          <button onClick={proceed} disabled={bookDisabled} className="w-full h-12 zc-btn-primary text-sm mt-6 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="vd-book-cta">
            {bookDisabled ? bookLabel : <>Book Now <ArrowRight size={14} /></>}
          </button>
          {FRACTIONAL_ENABLED && v.has_offering && v.offering_id && (
            <button onClick={() => navigate(`/invest/${v.offering_id}`)} className="w-full h-12 zc-btn-outline-secondary text-sm mt-3 flex items-center justify-center gap-2" data-testid="vd-invest-cta">
              Invest in this car
            </button>
          )}
          <PriceWatchButton vehicleId={id} start={start} end={end} />

          <div className="mt-4 zc-hint">
            You won't be charged yet. Funds are held in escrow until trip completion.
          </div>
        </aside>
      </div>

      <div
        className="fixed bottom-0 inset-x-0 z-40 border-t border-[var(--zc-border)] bg-[var(--zc-bg)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden"
        data-testid="vd-mobile-sticky-bar"
      >
        <div className="flex items-center gap-3 max-w-[1400px] mx-auto">
          <div className="min-w-0 flex-1">
            <div className="eyebrow">Trip</div>
            <div className="text-sm text-[var(--zc-text)] truncate">
              {start && end ? formatTripRange(start, end) : "Select dates"}
            </div>
            {days > 0 && (
              <div className="zc-price text-lg mt-0.5">{money(estimatedTotal)}</div>
            )}
          </div>
          <button
            type="button"
            onClick={proceed}
            disabled={bookDisabled}
            className="shrink-0 min-h-11 px-5 zc-btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="vd-mobile-book-cta"
          >
            {bookDisabled ? bookLabel : <>Book now <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function PriceWatchButton({ vehicleId, start, end }) {
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const ok = !!start && !!end;
  const click = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(`${API}/price-watches`, {
        vehicle_id: vehicleId, start_date: start, end_date: end,
      }, { withCredentials: true });
      setDone(true);
      toast.success("We'll alert you if the price drops on these dates");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Please sign in to track price drops");
      } else {
        toast.error(e.response?.data?.detail || "Couldn't set up alert");
      }
    } finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="w-full h-11 mt-3 grid place-items-center text-xs font-semibold rounded-full" style={{ background: "var(--zc-secondary-soft)", color: "var(--zc-secondary)" }} data-testid="vd-watch-done">
        ✓ You'll be alerted on price drops
      </div>
    );
  }
  return (
    <button
      onClick={click}
      disabled={!ok || busy}
      className="w-full h-11 zc-btn-ghost text-xs mt-3 flex items-center justify-center gap-2 disabled:opacity-50"
      data-testid="vd-watch-cta"
      title={ok ? "Get alerted when these dates get cheaper" : "Pick dates first"}
    >
      <Sparkles size={12} /> {busy ? "Setting up…" : "Alert me on price drops"}
    </button>
  );
}
